import type { Element as SlateElement, Editor, NodeEntry } from 'slate';
import { Text, Node } from 'slate';
import { normalizeUrl } from '@/scripts/utils';
import type {
  DeserializePayloadRender,
  SerializePayloadOptions,
} from '../../../chat-input/helpers';
import { repairSelection } from '../../scripts/utils';
import type { LinkElement } from './types';
import { LinkElementType } from './types';
import LinkBase from './components/link-base';

/**
 * protocol: (https?://)?
 * user: (\\w+:\\w+@)?
 * domain: ([0-9a-z-@]+\\.)+[a-z]{2,63}
 * ip: (\\d+\\.){3}\\d+
 * port: (:\\d{1,5})?
 * path: (/[^/\\s?#]*)*
 * query: ([?#][^\\s]*)?
 * integrality: (?![.:@a-z0-9]+)
 */

const urlChar =
  '(https?://)?(\\w+:\\w+@)?(([0-9a-z-@]+\\.)+[a-z]{2,63}|(\\d+\\.){3}\\d+)(:\\d{1,5})?(/[^/\\s?#]*)*([?#][^\\s]*)?(?![.:@a-z0-9]+)';

const urlAbsoluteReg = new RegExp(`^${urlChar}$`, 'i');

const urlContainReg = new RegExp(`\\b${urlChar}\\b`, 'i');

function parsePart(
  str: string,
  callback: (part: string, isUrl: boolean, index: number) => void
): void {
  const urlCharReg = new RegExp(`\\b${urlChar}\\b`, 'ig');
  let lastIndex = 0;
  let index = 0;

  let match: RegExpExecArray | null;
  while ((match = urlCharReg.exec(str)) !== null) {
    if (match.index > lastIndex) {
      const nonUrl = str.substring(lastIndex, match.index);
      callback(nonUrl, false, index++);
    }

    const url = match[0];
    callback(url, true, index++);
    lastIndex = match.index + url.length;
  }

  if (lastIndex < str.length) {
    const remaining = str.substring(lastIndex);
    callback(remaining, false, index);
  }
}

export function isLinkElement(element: SlateElement) {
  return element.type === LinkElementType;
}

export function withLinkEditor(editor: Editor) {
  const { isInline, normalizeNode } = editor;

  const rs = repairSelection(editor);

  editor.isInline = (element: SlateElement) => {
    return isLinkElement(element) || isInline(element);
  };

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (Text.isText(node)) {
      const text = Node.string(node);

      const isAbsoluteUrl = urlAbsoluteReg.test(text);
      const linkEntry = editor.above({
        at: path,
        match: isLinkElement as any,
      });

      // Change LinkElement url
      if (linkEntry && isAbsoluteUrl) {
        editor.setNodes(
          {
            url: normalizeUrl(text),
          },
          {
            at: linkEntry[1],
          }
        );
        return;
      }

      // Abnormal url LinkElement
      if (linkEntry && !isAbsoluteUrl) {
        editor.unwrapNodes({
          at: path,
          match: isLinkElement as any,
        });
        return;
      }

      // Normal url text
      if (!linkEntry && isAbsoluteUrl) {
        editor.wrapNodes(
          {
            type: LinkElementType,
            url: normalizeUrl(text),
            children: [{ text: text }],
          },
          { at: path }
        );
        return;
      }

      // Contain normal url text
      const isContainUrl = urlContainReg.test(text);
      if (isContainUrl) {
        const nodes: Node[] = [];

        parsePart(text, (part) => {
          nodes.push({ text: part });
        });

        rs.save();
        editor.removeNodes({ at: path });
        editor.insertNodes(nodes, {
          at: path,
        });
        rs.repair();
        return;
      }
    }

    normalizeNode(entry);
  };

  return editor;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface SerializePayloadOptions_link extends SerializePayloadOptions {}

export function serializePayload_link({
  editor,
  entry: [node, path],
  callback,
}: SerializePayloadOptions_link) {
  const result = editor.above({
    at: path,
    match: isLinkElement as any,
  }) as NodeEntry<LinkElement>;

  if (result) {
    const [{ url }] = result;

    if (url) {
      callback({
        part: (node as Text).text,
        entity: {
          type: LinkElementType,
          url,
        },
      });
    }

    return true;
  }
}

export const deserializePayloadRender_link: DeserializePayloadRender = ({
  type,
  text,
  entity,
  key,
}) => {
  if (type === LinkElementType) {
    return {
      children: (
        <LinkBase key={key} href={entity.url}>
          {text}
        </LinkBase>
      ),
    };
  }
};
